import React, { Component } from 'react';
import { Container } from 'bloomer/lib/layout/Container';
import { Box } from 'bloomer/lib/elements/Box';
import { RouteComponentProps } from '@reach/router';
import LoadingSpinner from '../elements/LoadingSpinner';
import DogMedicalData from '../components/DogMedicalData';
import api from '../util/api';
import { Title } from 'bloomer/lib/elements/Title';
import moment, { Moment } from 'moment';
import { Button } from 'bloomer/lib/elements/Button';
import DatePicker from '../elements/SingleDatePicker';
import Spacer from '../elements/Spacer';
import styled from 'styled-components';
import { popupErrorFromServer } from '../util/helperFunctions';

const sortFuncs = {
  anumber: (a: [string, MedicalData[]], b: [string, MedicalData[]]) => parseInt(a[0], 10) - parseInt(b[0], 10),
  name: (a: [string, MedicalData[]], b: [string, MedicalData[]]) =>
    a[1][0].animalName.localeCompare(b[1][0].animalName),
};

interface Params {}

interface PropTypes extends RouteComponentProps<Params> {}
interface StateTypes {
  dogs: { [aNumber: string]: MedicalData[] };
  date: Moment;
  sort: (a: [string, MedicalData[]], b: [string, MedicalData[]]) => number;
  focused: boolean;
  isLoading: boolean;
}

const VerticalCenter = styled.div`
  display: flex;
  align-items: center;
  margin-top: 15px;
`;
class MedicalDataPage extends Component<PropTypes, StateTypes> {
  state: StateTypes = {
    dogs: {},
    date: moment(),
    sort: sortFuncs['name'],
    focused: false,
    isLoading: true,
  };

  handleFocusChange = ({ focused }: { focused: boolean | null }) => this.setState({ focused: focused ? true : false });

  handleDateChange = (date: moment.Moment | null) => {
    if (date) {
      this.setState(
        {
          date,
        },
        this.getDataForDate,
      );
    }
  };

  switchSort = (type: 'anumber' | 'name') => () => this.setState({ sort: sortFuncs[type] });

  getDataForDate = async () => {
    try {
      this.setState({ isLoading: true });
      const { date } = this.state;
      const medData = await api.medicalData.get({
        startDate: date.startOf('day').toISOString(),
        endDate: date.endOf('day').toISOString(),
      });

      const indexedMedData: {
        [aNumber: string]: MedicalData[];
      } = medData.reduce((obj: { [aNumber: string]: MedicalData[] }, entry) => {
        const newObj = { ...obj };
        if (newObj[entry.aNumber]) {
          newObj[entry.aNumber].push(entry);
          return newObj;
        }
        newObj[entry.aNumber] = [entry];
        return newObj;
      }, {});

      this.setState({
        dogs: indexedMedData,
        isLoading: false,
      });
    } catch (err) {
      popupErrorFromServer(err);
    }
  };

  componentDidMount() {
    this.getDataForDate();
  }

  render() {
    const { dogs, date, sort, focused, isLoading } = this.state;
    const dogsArr = Object.entries(dogs).sort(sort);
    let content = <LoadingSpinner />;

    if (dogsArr.length && !isLoading) {
      content = (
        <>
          {dogsArr.map(([aNumber, medDataArr]) => (
            <DogMedicalData key={`med-${aNumber}`} aNumber={aNumber} medicalData={medDataArr} />
          ))}
        </>
      );
    } else if (!isLoading) {
      content = <p>No Data Found.</p>;
    }

    return (
      <Container>
        <Box>
          <Title>Medical Data for {date.format('dddd MMMM Do YYYY')}</Title>
          <Container isFluid={true}>
            <div>
              <Spacer>Select Date:</Spacer>{' '}
              <DatePicker
                id="medical-data-date"
                date={date}
                focused={focused}
                onDateChange={this.handleDateChange}
                onFocusChange={this.handleFocusChange}
                showClearDate={false}
              />
            </div>
            <VerticalCenter>
              <Spacer>Sort:</Spacer>
              <Spacer>
                <Button onClick={this.switchSort('name')} isColor="primary">
                  A-Z
                </Button>
              </Spacer>
              <Spacer>
                <Button onClick={this.switchSort('anumber')} isColor="primary">
                  A#1-A#9
                </Button>
              </Spacer>
            </VerticalCenter>
          </Container>
          {content}
        </Box>
      </Container>
    );
  }
}

export default MedicalDataPage;
